import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { APP_CONFIG } from '../../config/constants';
import Sidebar from './Sidebar';
import TopBar from './TopBar';
import CommandPalette from '../ui/CommandPalette';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiHome, FiCheckSquare, FiTarget, FiFeather, FiEdit3 } = FiIcons;

const PAGE_TITLES = {
  '/': 'Overview',
  '/plan': 'Plan',
  '/studio': 'Studio',
  '/journal': 'Journal',
  '/membership': 'Membership',
  '/garden': 'Garden',
  '/production': 'Production',
  '/upgrades': 'Upgrades',
  '/marketplace': 'Marketplace',
  '/gatekeeper': 'Gatekeeper AI',
  '/focus': 'Focus Session',
  '/streak': 'Streak & Daily Claim',
  '/watering': 'Daily Watering',
  '/plots': 'Garden Plot',
  '/dividend-garden': 'Dividend Garden',
  '/thought-garden': 'Thought Garden',
  '/habit-garden': 'Habit Garden',
  '/the-greenhouse': 'The Greenhouse',
  '/weed-list': 'Weed List',
  '/calendar': 'Calendar',
  '/planner': 'Daily Planner',
  '/the-grove': 'The Grove',
  '/training-cards': 'Training Cards',
  '/analytics': 'Analytics',
  '/settings': 'Settings',
};

const MOBILE_ROUTES = [
  { to: '/', label: 'Today', icon: FiHome },
  { to: '/plan', label: 'Plan', icon: FiCheckSquare },
  { to: '/studio', label: 'Studio', icon: FiTarget },
  { to: '/garden', label: 'Garden', icon: FiFeather },
  { to: '/journal', label: 'Journal', icon: FiEdit3 },
];

function MobileBottomNav() {
  return (
    <nav
      aria-label="Primary navigation"
      className="fixed inset-x-0 bottom-0 z-30 border-t border-pine/10 bg-paper/95 pb-[env(safe-area-inset-bottom)] backdrop-blur lg:hidden"
    >
      <div className="grid grid-cols-5">
        {MOBILE_ROUTES.map(({ to, label, icon }) => (
          <NavLink
            key={to}
            to={to}
            end={to === '/'}
            className={({ isActive }) => (
              `flex min-h-[56px] min-w-[44px] flex-col items-center justify-center gap-1 px-1 text-[11px] font-bold ${
                isActive ? 'text-pine' : 'text-pine/55'
              }`
            )}
          >
            {({ isActive }) => (
              <>
                <SafeIcon
                  icon={icon}
                  className={`h-5 w-5 ${isActive ? 'text-sage' : ''}`}
                />
                <span>{label}</span>
              </>
            )}
          </NavLink>
        ))}
      </div>
    </nav>
  );
}

export default function Layout({ children }) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const pageTitle = PAGE_TITLES[location.pathname] || 'MindGarden';

  const handleOpenMobileMenu = () => {
    setMobileMenuOpen(true);
  };

  const handleCloseMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  // Check if current route should have full-width layout
  const isFullWidthRoute = APP_CONFIG.routes.fullWidth.includes(location.pathname);

  if (isFullWidthRoute) {
    if (location.pathname === '/legacy-leo/presentation') {
        return <main className="w-full h-full">{children}</main>; // Fullscreen for presentation
    }
    return (
      <div className="fixed inset-0 w-full h-full bg-gradient-to-br from-beige via-white to-sage/10">
        <CommandPalette />
        {/* Mobile Overlay */}
        {mobileMenuOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={handleCloseMobileMenu}
            aria-hidden="true"
          />
        )}

        {/* Sidebar - Only show on mobile when menu is open */}
        <div
          className={`
            fixed inset-y-0 left-0 z-50 transform transition-transform duration-300 ease-in-out lg:hidden
            ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
          `}
        >
          <Sidebar
            collapsed={false}
            mobileOpen={true}
            onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
            onCloseMobile={handleCloseMobileMenu}
          />
        </div>

        {/* TopBar for mobile menu trigger only */}
        <div className="fixed top-0 left-0 right-0 z-20 lg:hidden">
          <TopBar
            title={pageTitle}
            onOpenMobileMenu={handleOpenMobileMenu}
            isFullWidth={true}
          />
        </div>

        {/* Main content - Full screen on desktop, with top padding on mobile */}
        <div className="w-full h-full pt-16 lg:pt-0 pb-[calc(3.5rem+env(safe-area-inset-bottom))] lg:pb-0">
          <main className="w-full h-full">{children}</main>
        </div>
        <MobileBottomNav />
      </div>
    );
  }

  // Regular layout with sidebar navigation for ALL other routes
  return (
    <div className="min-h-screen bg-gradient-to-br from-beige via-white to-sage/10 relative">
      <CommandPalette />
      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={handleCloseMobileMenu}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <Sidebar
        collapsed={sidebarCollapsed}
        mobileOpen={mobileMenuOpen}
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
        onCloseMobile={handleCloseMobileMenu}
      />

      {/* Main Content */}
      <div
        className={`
          transition-all duration-300 ease-in-out
          ${sidebarCollapsed ? 'lg:ml-18' : 'lg:ml-60'}
          min-h-screen flex flex-col
        `}
      >
        <TopBar title={pageTitle} onOpenMobileMenu={handleOpenMobileMenu} />
        <main className="flex-1 p-4 lg:p-6 pb-[calc(4.5rem+env(safe-area-inset-bottom))] lg:pb-6">
          <div className="max-w-7xl mx-auto w-full h-full">{children}</div>
        </main>
      </div>
      <MobileBottomNav />
    </div>
  );
}